import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { blogPosts } from '../data/blogData';
import { toast } from 'react-toastify';
import SEOHead from '../components/SEO/SEOHead';
import {
    Container,
    Typography,
    Box,
    Chip,
    Button,
    Divider,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Avatar,
    alpha
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShareIcon from '@mui/icons-material/Share';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { createArticleSchema, createFAQSchema, createBreadcrumbSchema, combineSchemas } from '../components/SEO/jsonLdTemplates';

const BlogPost = () => {
    const { slug } = useParams();
    const post = blogPosts.find(p => p.slug === slug);

    if (!post) {
        return (
            <Container sx={{ py: 20, textAlign: 'center' }}>
                <Typography variant="h4" color="white">Post not found</Typography>
                <Button component={Link} to="/blog" sx={{ mt: 2, color: '#007AFF' }}>Back to Blog</Button>
            </Container>
        );
    }

    // Create JSON-LD schema
    const articleSchema = createArticleSchema({
        headline: post.title,
        description: post.excerpt,
        image: post.image,
        datePublished: post.date,
        author: post.author,
        url: `https://verveai.co/blog/${post.slug}`
    });

    const breadcrumbSchema = createBreadcrumbSchema([
        { name: 'Home', url: 'https://verveai.co/' },
        { name: 'Blog', url: 'https://verveai.co/blog' },
        { name: post.title, url: `https://verveai.co/blog/${post.slug}` }
    ]);

    const faqSchema = post.faqs ? createFAQSchema(post.faqs) : null;

    const combinedSchema = combineSchemas(
        articleSchema,
        breadcrumbSchema,
        faqSchema
    );

    return (
        <>
            <SEOHead
                title={`${post.title} | VerveAI Blog`}
                description={post.excerpt}
                canonical={`https://verveai.co/blog/${post.slug}`}
                jsonLd={combinedSchema}
                image={post.image}
                type="article"
            />

            <Box sx={{ pt: 12, pb: 8 }}>
                <Container maxWidth="md">
                    {/* Top Navigation */}
                    <Button
                        component={Link}
                        to="/blog"
                        startIcon={<ArrowBackIcon />}
                        sx={{ mb: 4, color: 'text.secondary', '&:hover': { color: '#007AFF' } }}
                    >
                        Back to Articles
                    </Button>

                    {/* Header Section */}
                    <Box component="header" sx={{ mb: 6, textAlign: 'center' }}>
                        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
                            {post.tags.map((tag) => (
                                <Chip
                                    key={tag}
                                    label={tag}
                                    sx={{
                                        backgroundColor: alpha('#007AFF', 0.1),
                                        color: '#007AFF',
                                        fontWeight: 600,
                                        border: `1px solid ${alpha('#007AFF', 0.2)}`
                                    }}
                                />
                            ))}
                        </Box>

                        <Typography
                            variant="h2"
                            component="h1"
                            gutterBottom
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '2rem', md: '3.5rem' },
                                lineHeight: 1.2,
                                mb: 3
                            }}
                        >
                            {post.title}
                        </Typography>

                        {/* Hero Image */}
                        {post.image && (
                            <Box
                                component="img"
                                src={post.image}
                                alt={post.title}
                                sx={{
                                    width: '100%',
                                    maxHeight: '400px',
                                    objectFit: 'cover',
                                    borderRadius: 4,
                                    mb: 4,
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                                    border: `1px solid ${alpha('#FFFFFF', 0.1)}`
                                }}
                            />
                        )}

                        <Stack
                            direction="row"
                            spacing={3}
                            justifyContent="center"
                            alignItems="center"
                            sx={{ color: 'text.secondary', fontSize: '0.9rem' }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#007AFF' }}>V</Avatar>
                                <Typography variant="subtitle2" color="text.primary">{post.author}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarTodayIcon sx={{ fontSize: 18 }} />
                                <span>{post.date}</span>
                            </Box>
                        </Stack>
                    </Box>

                    {/* Main Content Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, md: 5 },
                            bgcolor: alpha('#0F141F', 0.6),
                            backdropFilter: 'blur(20px)',
                            border: `1px solid ${alpha('#FFFFFF', 0.05)}`,
                            borderRadius: 4
                        }}
                    >
                        {/* Intro/Excerpt */}
                        <Typography variant="h6" sx={{ mb: 4, fontStyle: 'italic', color: 'text.secondary', borderLeft: '4px solid #007AFF', pl: 2, py: 1 }}>
                            {post.excerpt}
                        </Typography>

                        {post.toc && (
                            <Box sx={{ mb: 6, p: 3, bgcolor: alpha('#FFFFFF', 0.03), borderRadius: 2, border: `1px solid ${alpha('#FFFFFF', 0.05)}` }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#F5F7FA' }}>
                                    Table of Contents
                                </Typography>
                                <Stack spacing={1}>
                                    {post.toc.map((item) => (
                                        <a
                                            key={item.id}
                                            href={`#${item.id}`}
                                            style={{
                                                textDecoration: 'none',
                                                color: '#B0B3C0',
                                                fontSize: '0.95rem',
                                                transition: 'color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.target.style.color = '#007AFF'}
                                            onMouseLeave={(e) => e.target.style.color = '#B0B3C0'}
                                        >
                                            • {item.label}
                                        </a>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />

                        {/* Article Body */}
                        <Box
                            component="article"
                            sx={{
                                typography: 'body1',
                                '& img': {
                                    maxWidth: '100%',
                                    height: 'auto',
                                    borderRadius: 8,
                                    my: 4,
                                    border: `1px solid ${alpha('#FFFFFF', 0.1)}`
                                },
                                '& h2': {
                                    fontSize: '2rem',
                                    fontWeight: 800,
                                    mt: 6,
                                    mb: 3,
                                    color: '#F5F7FA',
                                    scrollMarginTop: '100px'
                                },
                                '& h3': {
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    mt: 5,
                                    mb: 2,
                                    color: '#F5F7FA',
                                    scrollMarginTop: '100px'
                                },
                                '& p': {
                                    mb: 3,
                                    lineHeight: 1.8,
                                    fontSize: '1.125rem',
                                    color: '#B0B3C0'
                                },
                                '& .bluf': {
                                    backgroundColor: alpha('#00E676', 0.08),
                                    color: '#F5F7FA',
                                    p: 2,
                                    borderRadius: 2,
                                    border: `1px solid ${alpha('#00E676', 0.2)}`
                                },
                                '& .verve-insight-box': {
                                    backgroundColor: alpha('#007AFF', 0.08),
                                    color: '#F5F7FA',
                                    p: 3,
                                    my: 4,
                                    borderRadius: 2,
                                    borderLeft: '4px solid #007AFF',
                                    fontStyle: 'italic',
                                    '& strong': { color: '#007AFF' }
                                },
                                '& ul, & ol': {
                                    mb: 3,
                                    pl: 4,
                                    '& li': {
                                        mb: 1.5,
                                        color: '#B0B3C0',
                                        fontSize: '1.125rem',
                                        lineHeight: 1.6
                                    }
                                },
                                '& strong': {
                                    color: '#F5F7FA',
                                    fontWeight: 600
                                },
                                '& blockquote': {
                                    borderLeft: '4px solid #00E676',
                                    m: 0,
                                    my: 4,
                                    pl: 3,
                                    py: 1,
                                    bgcolor: alpha('#00E676', 0.05),
                                    fontStyle: 'italic',
                                    color: 'text.primary'
                                }
                            }}
                            dangerouslySetInnerHTML={{ __html: post.content }}
                        />

                        {/* Conclusion */}
                        {post.conclusion && (
                            <Box sx={{ mt: 6, p: 4, bgcolor: alpha('#007AFF', 0.05), borderRadius: 3, border: `1px solid ${alpha('#007AFF', 0.1)}` }}>
                                <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 700, color: '#F5F7FA' }}>
                                    Conclusion
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: '1.125rem', color: '#B0B3C0' }}>
                                    {post.conclusion}
                                </Typography>
                            </Box>
                        )}

                        {/* FAQs Section */}
                        {post.faqs && post.faqs.length > 0 && (
                            <Box sx={{ mt: 8 }}>
                                <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 700, mb: 4, color: '#F5F7FA' }}>
                                    Frequently Asked Questions
                                </Typography>
                                {post.faqs.map((faq, index) => (
                                    <Accordion
                                        key={index}
                                        sx={{
                                            bgcolor: 'rgba(255,255,255,0.03)',
                                            color: '#F5F7FA',
                                            mb: 1,
                                            border: '1px solid rgba(255,255,255,0.05)',
                                            '&:before': { display: 'none' },
                                            '&.Mui-expanded': { bgcolor: 'rgba(255,255,255,0.05)' }
                                        }}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#007AFF' }} />}>
                                            <Typography sx={{ fontWeight: 600 }}>{faq.question}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography color="text.secondary">
                                                {faq.answer}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        )}

                    </Paper>

                    {/* Share & Social Footer */}
                    <Box sx={{ mt: 8, pt: 4, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        <Typography variant="h6" sx={{ color: '#F5F7FA' }}>
                            Share this article
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="outlined"
                                startIcon={<ShareIcon />}
                                onClick={() => {
                                    if (navigator.share) {
                                        navigator.share({
                                            title: post.title,
                                            text: post.excerpt,
                                            url: window.location.href,
                                        });
                                    } else {
                                        navigator.clipboard.writeText(window.location.href);
                                        toast.success('Link copied to clipboard!');
                                    }
                                }}
                                sx={{
                                    color: '#B0B3C0',
                                    borderColor: 'rgba(255,255,255,0.2)',
                                    '&:hover': { borderColor: '#007AFF', color: '#007AFF' }
                                }}
                            >
                                Share
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}
                                sx={{
                                    color: '#0077b5',
                                    borderColor: 'rgba(0,119,181,0.2)',
                                    '&:hover': { borderColor: '#0077b5', bgcolor: 'rgba(0,119,181,0.1)' }
                                }}
                            >
                                LinkedIn
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`, '_blank')}
                                sx={{
                                    color: '#1DA1F2',
                                    borderColor: 'rgba(29,161,242,0.2)',
                                    '&:hover': { borderColor: '#1DA1F2', bgcolor: 'rgba(29,161,242,0.1)' }
                                }}
                            >
                                Twitter
                            </Button>
                        </Stack>
                    </Box>
                </Container >
            </Box >
        </>
    );
};

export default BlogPost;
